import { dateFromObjectId, Phone, Tooltip } from "@xola/ui-kit";
import { ColDef } from "ag-grid-community";
import { DateFormat, formatDate } from "common";
import { last, sortBy, uniqueId } from "lodash";
import React from "react";
import { MessageObject, Timeslot, WaitlistItem } from "types";
import { DemographicsPopover } from "../RosterGuestList/DemographicCell";
import { CatalogInfo } from "../RosterGuestTypes";
import { WaitlistActionButton } from "./WaitlistActionButton";
import { CopyableField } from "@/components/Common/CopyableField";
import { NoTranslate } from "@/components/Common/NoTranslate";

const defaultColDef: ColDef = {
    resizable: false,
    filter: false,
    menuTabs: [],
    suppressMovable: true,
    suppressAutoSize: true,
    flex: 1,
};

export enum WaitlistCell {
    ACTION = "Actions",
    GUEST_NAME = "Guest Name",
    GUEST_COUNT = "Guest Count",
    EMAIL = "Email",
    PHONE = "Phone",
    WAITLIST_DATE = "Waitlist Date",
    INVITE_SENT = "Invite Sent",
}

interface CellProps {
    waitlist: WaitlistItem;
    timeslot: Timeslot;
    demographics: CatalogInfo;
    isEquipment?: boolean;
}

export const popoverWaitlistParentId = uniqueId("popoverWaitlistParentId");

const ActionButtonRenderer = ({ data }: { data: CellProps }) => {
    return (
        <span data-testid="roster-waitlist-list-action-button">
            <WaitlistActionButton
                timeslot={data.timeslot}
                item={data.waitlist}
                purchase={data.waitlist.purchase}
                popoverParentId={popoverWaitlistParentId}
            />
        </span>
    );
};

export const PhoneCellRenderer = ({ data }: { data: CellProps }) => {
    if (!data.waitlist.purchase.phone) {
        return null;
    }

    const { phone } = data.waitlist.purchase;

    return (
        <CopyableField data={phone} dataTestId="dashboard-roster-waitlist-phone" classNames={{ badge: "w-14" }}>
            <Phone>{phone}</Phone>
        </CopyableField>
    );
};

export const EmailCellRenderer = ({ data }: { data: CellProps }) => {
    if (!data.waitlist.purchase.customerEmail) {
        return null;
    }

    const email = data.waitlist.purchase.customerEmail;

    return (
        <CopyableField
            data={email}
            dataTestId="dashboard-roster-waitlist-customer-email"
            classNames={{ badge: "w-14" }}
        >
            <Phone>{email}</Phone>
        </CopyableField>
    );
};

export const GuestNameCellRenderer = ({ data }: { data: CellProps }) => {
    if (!data.waitlist.purchase.customerName) {
        return null;
    }

    const name = data.waitlist.purchase.customerName;

    return <NoTranslate data-testid="dashboard-roster-waitlist-name">{name}</NoTranslate>;
};

export const WaitlistDateCellRenderer = ({ data }: { data: CellProps }) => {
    const waitlistDate = formatDate(data.waitlist.createdAt, DateFormat.DATE_TIME);

    return (
        <Tooltip content={<NoTranslate>{waitlistDate}</NoTranslate>}>
            <NoTranslate data-testid="dashboard-roster-waitlist-created-at">{waitlistDate}</NoTranslate>
        </Tooltip>
    );
};

export const InviteSentCellRenderer = ({ data }: { data: CellProps }) => {
    const invitationMessages = data.waitlist.purchase.conversation?.messages?.filter((message) => {
        return message.object === MessageObject.WAITLIST_INVITATION;
    });

    const lastInvitationMessage = last(sortBy(invitationMessages, (message) => dateFromObjectId(message.id).unix()));

    const text = lastInvitationMessage ? (
        <div>
            Invitation sent{" "}
            <var data-var="lastInvitationMessageDuration">{dateFromObjectId(lastInvitationMessage.id).fromNow()}</var>
        </div>
    ) : (
        <span>--</span>
    );
    return (
        <Tooltip content={text} disabled={!lastInvitationMessage}>
            {text}
        </Tooltip>
    );
};

export const GuestsCountCellRenderer = ({ data }: { data: CellProps }) => {
    return (
        <div>
            <DemographicsPopover
                demographics={data.demographics}
                testId="dashboard-roster-waitlist-quantity"
                unitLabel={data.isEquipment ? "Equipment" : "Guests"}
            />
        </div>
    );
};

export const columnDefs = [
    {
        ...defaultColDef,
        headerName: WaitlistCell.ACTION,
        maxWidth: 100,
        cellRenderer: ActionButtonRenderer,
    },
    {
        ...defaultColDef,
        headerName: WaitlistCell.GUEST_NAME,
        cellRenderer: GuestNameCellRenderer,
    },
    {
        ...defaultColDef,
        headerName: WaitlistCell.GUEST_COUNT,
        cellRenderer: GuestsCountCellRenderer,
    },
    {
        ...defaultColDef,
        headerName: WaitlistCell.EMAIL,
        cellRenderer: EmailCellRenderer,
    },
    {
        ...defaultColDef,
        headerName: WaitlistCell.PHONE,
        cellRenderer: PhoneCellRenderer,
    },
    {
        ...defaultColDef,
        headerName: WaitlistCell.WAITLIST_DATE,
        cellRenderer: WaitlistDateCellRenderer,
    },
    {
        ...defaultColDef,
        headerName: WaitlistCell.INVITE_SENT,
        cellRenderer: InviteSentCellRenderer,
    },
];
